import { getAuthToken } from '../../services/auth/tokenStorage';
import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  TextInput,
  Modal,
  Pressable,
  Image,
  ScrollView,
  Alert,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { ms, s, vs } from '../../utils/responsive';
import { NavigationProp, useFocusEffect } from '@react-navigation/native';
import { launchCamera, launchImageLibrary, ImagePickerResponse, CameraOptions, ImageLibraryOptions } from 'react-native-image-picker';
import DateTimePickerModal from 'react-native-modal-datetime-picker';
import { API_URL } from '../../config';
import UserImageCircle from '../../components/UserImageCircle';
import { getCachedProfileImage, removeCachedProfileImage, setCachedProfileImage } from '../../services/profileImageCache';

// Import default profile image
import defaultProfileImage from '../../assets/icons/profile.png';

// Import icons
import backIcon from '../../assets/icons/back.png';
import cameraIcon from '../../assets/icons/camera.png';
import galleryIcon from '../../assets/icons/gallery.png';
import trashIcon from '../../assets/icons/trash.png';
import editIcon from '../../assets/icons/edit.png';
import lockIcon from '../../assets/icons/lock.png';
import settingsIcon from '../../assets/icons/settings.png';

import { createLogger } from '../../utils/logger';
const logger = createLogger('EditProfile');

interface EditProfileScreenProps {
  navigation: NavigationProp<any>;
}

// Format phone number as (XXX) XXX-XXXX
const formatPhoneNumber = (text: string): string => {
  // Remove all non-numeric characters
  const cleaned = text.replace(/\D/g, '');

  // Format as (XXX) XXX-XXXX
  if (cleaned.length <= 3) {
    return cleaned;
  } else if (cleaned.length <= 6) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
  } else {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  }
};

// Strip phone formatting for storage
const stripPhoneFormatting = (phone: string): string => {
  return phone.replace(/\D/g, '');
};

const EditProfileScreen = ({ navigation }: EditProfileScreenProps) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [dob, setDob] = useState<Date | null>(null);
  const [datePickerVisible, setDatePickerVisible] = useState(false);
  const [showPhotoOptions, setShowPhotoOptions] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [originalUsername, setOriginalUsername] = useState(''); // To track username changes
  const [profileImageUri, setProfileImageUri] = useState<string | null>(null);

  // Load user data when screen focuses
  useFocusEffect(
    React.useCallback(() => {
      loadUserData();
      loadProfileImage();
    }, [])
  );

  // Convert image URI to base64 data URI
  const convertImageToBase64 = async (uri: string): Promise<string | null> => {
    try {
      const response = await fetch(uri);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          resolve(reader.result as string);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      logger.error('Error converting image to base64:', error);
      return null;
    }
  };

  const loadProfileImage = async () => {
    try {
      const token = await getAuthToken();

      // Try to load from backend first
      if (token) {
        try {
          const response = await fetch(`${API_URL}/users/profile-image`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });

          if (response.ok) {
            const data = await response.json();
            if (data.success && data.imageData) {
              await setCachedProfileImage(data.imageData);
              setProfileImageUri(data.imageData);
              return;
            }

            if (data.success) {
              await removeCachedProfileImage();
              setProfileImageUri(null);
              return;
            }
          }
        } catch (backendError) {
          logger.debug('Could not load from backend, trying local cache:', backendError);
        }
      }

      const savedImageUri = await getCachedProfileImage();
      setProfileImageUri(savedImageUri);
    } catch (error) {
      logger.error('Error loading profile image:', error);
    }
  };

  const saveProfileImage = async (uri: string) => {
    try {
      // Convert image URI to base64
      const base64Data = await convertImageToBase64(uri);
      if (!base64Data) {
        Alert.alert('Error', 'Failed to process image');
        return;
      }

      // Save to local cache immediately for quick display
      await setCachedProfileImage(base64Data);
      setProfileImageUri(base64Data);

      // Upload to backend
      const token = await getAuthToken();
      if (token) {
        try {
          const response = await fetch(`${API_URL}/users/profile-image`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ imageData: base64Data }),
          });

          if (!response.ok) {
            logger.warn('Backend profile image upload failed, but local cache saved');
          }
        } catch (backendError) {
          logger.warn('Could not upload to backend, but local cache saved:', backendError);
        }
      }
    } catch (error) {
      logger.error('Error saving profile image:', error);
    }
  };

  const removeProfileImage = async () => {
    try {
      // Remove from local cache
      await removeCachedProfileImage();
      setProfileImageUri(null);
      setShowPhotoOptions(false);

      // Remove from backend
      const token = await getAuthToken();
      if (token) {
        try {
          await fetch(`${API_URL}/users/profile-image`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
        } catch (backendError) {
          logger.warn('Could not delete from backend:', backendError);
        }
      }

      Alert.alert('Success', 'Profile photo removed');
    } catch (error) {
      logger.error('Error removing profile image:', error);
      Alert.alert('Error', 'Failed to remove profile photo');
    }
  };

  const handleImageResponse = (response: ImagePickerResponse) => {
    setShowPhotoOptions(false);

    if (response.didCancel) {
      logger.debug('User cancelled image picker');
      return;
    }

    if (response.errorCode) {
      logger.error('ImagePicker Error:', response.errorMessage);
      Alert.alert('Error', response.errorMessage || 'Failed to select image');
      return;
    }

    if (response.assets && response.assets.length > 0) {
      const selectedImage = response.assets[0];
      if (selectedImage.uri) {
        saveProfileImage(selectedImage.uri);
        Alert.alert('Success', 'Profile photo updated');
      }
    }
  };

  const handleCameraPress = async () => {
    const options: CameraOptions = {
      mediaType: 'photo',
      quality: 0.8,
      maxWidth: 500,
      maxHeight: 500,
      saveToPhotos: false,
      cameraType: 'front',
    };

    try {
      const result = await launchCamera(options);
      handleImageResponse(result);
    } catch (error) {
      logger.error('Camera error:', error);
      Alert.alert('Error', 'Failed to open camera. Please check camera permissions.');
    }
  };

  const handleGalleryPress = async () => {
    const options: ImageLibraryOptions = {
      mediaType: 'photo',
      quality: 0.8,
      maxWidth: 500,
      maxHeight: 500,
      selectionLimit: 1,
    };

    try {
      const result = await launchImageLibrary(options);
      handleImageResponse(result);
    } catch (error) {
      logger.error('Gallery error:', error);
      Alert.alert('Error', 'Failed to open gallery. Please check photo library permissions.');
    }
  };

  const loadUserData = async () => {
    try {
      setIsLoading(true);
      const token = await getAuthToken();
      if (!token) {
        Alert.alert('Error', 'No authentication token found');
        navigation.goBack();
        return;
      }

      const response = await fetch(`${API_URL}/users/profile`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.user) {
          setFirstName(result.user.firstName || '');
          setLastName(result.user.lastName || '');
          setUsername(result.user.username || '');
          setEmail(result.user.email || '');
          setPhone(formatPhoneNumber(result.user.phoneNumber || ''));
          setOriginalUsername(result.user.username || '');
          // dateOfBirth arrives as a "YYYY-MM-DD" string (Postgres DATE).
          // Parse into a Date for the picker; null/undefined means not set.
          if (result.user.dateOfBirth) {
            const parsed = new Date(result.user.dateOfBirth);
            setDob(isNaN(parsed.getTime()) ? null : parsed);
          } else {
            setDob(null);
          }
        }
      } else {
        Alert.alert('Error', 'Failed to load profile data');
      }
    } catch (error) {
      logger.error('Error loading user data:', error);
      Alert.alert('Error', 'Network error loading profile data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveChanges = async () => {
    if (isSaving) return;

    // Validation
    if (!firstName.trim() || !lastName.trim()) {
      Alert.alert('Error', 'First name and last name are required');
      return;
    }

    if (!email.trim()) {
      Alert.alert('Error', 'Email is required');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      Alert.alert('Error', 'Please enter a valid email address');
      return;
    }

    try {
      setIsSaving(true);
      const token = await getAuthToken();
      if (!token) {
        Alert.alert('Error', 'No authentication token found');
        return;
      }

      // Check username availability if changed
      if (username !== originalUsername && username.trim()) {
        const usernameResponse = await fetch(`${API_URL}/users/check-username?username=${encodeURIComponent(username)}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (usernameResponse.ok) {
          const usernameResult = await usernameResponse.json();
          if (!usernameResult.available) {
            Alert.alert('Error', 'Username is already taken');
            return;
          }
        }
      }

      // Update profile
      const response = await fetch(`${API_URL}/users/update-profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          username: username.trim(),
          email: email.trim(),
          phoneNumber: stripPhoneFormatting(phone),
          // Send date in YYYY-MM-DD format (Postgres DATE).
          // Null clears the field; undefined would skip the update.
          dateOfBirth: dob ? dob.toISOString().split('T')[0] : null,
        }),
      });

      if (response.ok) {
        Alert.alert('Success', 'Profile updated successfully', [
          { text: 'OK', onPress: () => navigation.goBack() }
        ]);
      } else {
        const errorResult = await response.json();
        Alert.alert('Error', errorResult.message || 'Failed to update profile');
      }
    } catch (error) {
      logger.error('Error saving profile:', error);
      Alert.alert('Error', 'Network error saving profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangePassword = () => {
    // Navigate to change password screen
    navigation.navigate('ChangePassword');
  };

  // Render profile image - show selected image, or default profile.png
  const renderProfileImage = () => {
    if (profileImageUri) {
      return (
        <Image
          source={{ uri: profileImageUri }}
          style={styles.profileImage}
        />
      );
    }
    return (
      <Image
        source={defaultProfileImage}
        style={styles.profileImage}
      />
    );
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()}>
          <Image source={backIcon} style={styles.headerIcon} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Edit Profile</Text>
        <TouchableOpacity onPress={() => navigation.navigate('Settings')}>
          <Image source={settingsIcon} style={styles.headerIcon} />
        </TouchableOpacity>
      </View>

      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        <View style={styles.contentContainer}>
          {isLoading && (
            <View style={styles.loadingOverlay}>
              <ActivityIndicator size="large" color="#29BFC0" />
              <Text style={styles.loadingText}>Loading profile...</Text>
            </View>
          )}
          <View style={styles.profileImageSection}>
            <View style={styles.profileImageContainer}>
              <UserImageCircle size={120}>
                {renderProfileImage()}
              </UserImageCircle>
              <TouchableOpacity
                style={styles.editImageButton}
                onPress={() => setShowPhotoOptions(true)}
              >
                <Image source={editIcon} style={styles.editIcon} />
              </TouchableOpacity>
            </View>
            <Text style={styles.name}>
              {isLoading ? 'Loading...' : `${firstName} ${lastName}`.trim() || 'User Name'}
            </Text>
          </View>

          <View style={styles.form}>
            <View style={styles.inputGroup}>
              <Text style={styles.label}>First Name</Text>
              <TextInput
                style={styles.input}
                value={firstName}
                onChangeText={setFirstName}
                placeholder="Enter your first name"
                placeholderTextColor="#A9A9A9"
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>Last Name</Text>
              <TextInput
                style={styles.input}
                value={lastName}
                onChangeText={setLastName}
                placeholder="Enter your last name"
                placeholderTextColor="#A9A9A9"
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>Username</Text>
              <TextInput
                style={styles.input}
                value={username}
                onChangeText={setUsername}
                placeholder="Enter your username"
                placeholderTextColor="#A9A9A9"
                autoCapitalize="none"
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>Email</Text>
              <TextInput
                style={styles.input}
                value={email}
                onChangeText={setEmail}
                placeholder="Enter your email"
                placeholderTextColor="#A9A9A9"
                keyboardType="email-address"
                autoCapitalize="none"
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>Date of Birth</Text>
              <Pressable
                style={styles.input}
                onPress={() => setDatePickerVisible(true)}
                accessibilityLabel="Select date of birth"
              >
                <Text style={dob ? styles.inputText : styles.inputPlaceholder}>
                  {dob ? dob.toLocaleDateString() : 'MM / DD / YYYY'}
                </Text>
              </Pressable>
              <DateTimePickerModal
                isVisible={datePickerVisible}
                mode="date"
                date={dob ?? undefined}
                maximumDate={new Date()}
                onConfirm={d => {
                  setDob(d);
                  setDatePickerVisible(false);
                }}
                onCancel={() => setDatePickerVisible(false)}
              />
            </View>

            <View style={styles.inputGroup}>
              <Text style={styles.label}>Phone Number</Text>
              <TextInput
                style={styles.input}
                value={phone}
                onChangeText={(text) => setPhone(formatPhoneNumber(text))}
                placeholder="(555) 555-5555"
                placeholderTextColor="#A9A9A9"
                keyboardType="phone-pad"
                maxLength={14}
              />
            </View>
          </View>

          <View style={styles.buttonContainer}>
            <TouchableOpacity
              style={styles.changePasswordButton}
              onPress={handleChangePassword}
            >
              <Image source={lockIcon} style={[styles.buttonIcon, { tintColor: '#FFFFFF' }]} />
              <Text style={styles.changePasswordText}>Change Password</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.saveButton, (isSaving || isLoading) && styles.saveButtonDisabled]}
              onPress={handleSaveChanges}
              disabled={isSaving || isLoading}
            >
              {isSaving ? (
                <ActivityIndicator color="#FFFFFF" />
              ) : (
                <Text style={styles.saveButtonText}>Save Changes</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </ScrollView>

      <Modal
        visible={showPhotoOptions}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowPhotoOptions(false)}
      >
        <Pressable
          style={styles.modalOverlay}
          onPress={() => setShowPhotoOptions(false)}
        >
          <View style={styles.photoOptionsContainer}>
            <Text style={styles.photoOptionsTitle}>Update New Profile Picture</Text>
            <Text style={styles.photoOptionsSubtitle}>
              Here are the recent photos to look, start choosing one of them
            </Text>
            <View style={styles.photoOptions}>
              <TouchableOpacity
                style={styles.photoOption}
                onPress={handleCameraPress}
              >
                <Image source={cameraIcon} style={[styles.optionIcon, { tintColor: '#29BFC0' }]} />
                <Text style={styles.photoOptionText}>Camera</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.photoOption}
                onPress={handleGalleryPress}
              >
                <Image source={galleryIcon} style={[styles.optionIcon, { tintColor: '#29BFC0' }]} />
                <Text style={styles.photoOptionText}>Gallery</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.photoOption}
                onPress={removeProfileImage}
              >
                <Image source={trashIcon} style={[styles.optionIcon, { tintColor: '#E85C5C' }]} />
                <Text style={[styles.photoOptionText, { color: '#E85C5C' }]}>
                  Remove Photo
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </Pressable>
      </Modal>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: ms(20),
    paddingVertical: ms(15),
    marginTop: ms(10),
    backgroundColor: '#F3F6F8',
  },
  headerTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
    color: '#333',
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    padding: ms(20),
    paddingTop: ms(60),
  },
  profileImageSection: {
    alignItems: 'center',
    marginBottom: ms(40),
  },
  profileImageContainer: {
    position: 'relative',
    marginBottom: ms(10),
  },
  editImageButton: {
    position: 'absolute',
    right: 0,
    bottom: 0,
    backgroundColor: '#FFFFFF',
    width: s(36),
    height: s(36),
    borderRadius: ms(18),
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: ms(1),
    borderColor: '#29BFC0',
  },
  profileImage: {
    width: s(120),
    height: s(120),
    borderRadius: ms(60),
  },
  name: {
    fontSize: ms(20, 0.3),
    fontWeight: 'bold',
    color: '#333',
    marginTop: ms(10),
  },
  form: {
    marginBottom: ms(20),
  },
  inputGroup: {
    marginBottom: ms(20),
  },
  label: {
    fontSize: ms(16, 0.3),
    color: '#6B7280',
    marginBottom: ms(8),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    paddingHorizontal: ms(20),
    paddingVertical: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
    textAlign: 'left',
    justifyContent: 'center',
  },
  inputText: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  inputPlaceholder: {
    fontSize: ms(16, 0.3),
    color: '#A9A9A9',
  },
  buttonContainer: {
    padding: ms(20),
    paddingBottom: ms(120),
  },
  changePasswordButton: {
    backgroundColor: '#F2994A',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    height: s(50),
    borderRadius: ms(25),
    marginBottom: ms(25),
  },
  changePasswordText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: 'bold',
    marginLeft: ms(8),
  },
  saveButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
    marginBottom: ms(20),
  },
  saveButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  saveButtonDisabled: {
    backgroundColor: '#A9A9A9',
    opacity: 0.7,
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  loadingText: {
    marginTop: ms(10),
    fontSize: ms(16, 0.3),
    color: '#29BFC0',
  },
  headerIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  editIcon: {
    width: s(20),
    height: s(20),
    tintColor: '#29BFC0',
  },
  buttonIcon: {
    width: s(20),
    height: s(20),
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  photoOptionsContainer: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    padding: ms(20),
  },
  photoOptionsTitle: {
    fontSize: ms(20, 0.3),
    fontWeight: 'bold',
    color: '#333',
    marginBottom: ms(10),
    textAlign: 'center',
  },
  photoOptionsSubtitle: {
    fontSize: ms(16, 0.3),
    color: '#666',
    textAlign: 'center',
    marginBottom: ms(20),
  },
  photoOptions: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    paddingVertical: ms(20),
  },
  photoOption: {
    alignItems: 'center',
  },
  photoOptionText: {
    marginTop: ms(8),
    fontSize: ms(14, 0.3),
    color: '#333',
  },
  optionIcon: {
    width: s(24),
    height: s(24),
  },
});

export default EditProfileScreen;
